import { useEffect, useMemo, useRef, useState } from 'react';
import { Container, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useIntl } from 'react-intl';

import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { Tile } from 'components/Layout/PageContainer';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { ReleaseNotesMonth } from 'models/releaseNotes';
import { getMonthKeys, getMonthLabel, hasMoreReleaseNotesMonths } from 'pages/release-notes/releaseNotes.utils';
import { useReleaseNotesService } from 'services/rest-api.service';
import { tbColors } from 'styles/colors/tbColors';
import { Button } from 'components/Button';

const INITIAL_MONTH_COUNT = 4;
const MONTHS_PER_PAGE = 4;

function ReleaseNoteMonthSection({ month, highlight }: { month: ReleaseNotesMonth; highlight: boolean }) {
  const monthLabel = getMonthLabel(month.month);

  return (
    <div
      className={
        highlight
          ? 'bg-turquoise-500 bg-opacity-[.08] p-4 rounded-xl col-span-12'
          : 'col-span-12 bg-snow-200 p-4 rounded-xl'
      }
      data-testid="release-notes-month-section"
    >
      <div className="flex items-center gap-2 pb-3">
        <Typography sx={{ fontSize: '1.125rem', fontWeight: 700 }}>{monthLabel}</Typography>
        {highlight && (
          <span
            className="inline-flex items-center rounded-full border-2 text-[11px] font-medium leading-4"
            style={{
              height: '25px',
              paddingLeft: '8px',
              paddingRight: '8px',
              borderColor: tbColors.all.denim[200],
              color: tbColors.all.ash[900],
            }}
          >
            New
          </span>
        )}
      </div>
      <div className="flex flex-col gap-3">
        {month.items.length > 0 ? (
          month.items.map(item => (
            <div key={item.key} className="p-4 bg-coolGrey-50 rounded-lg cursor-default">
              <Typography fontSize="16px" sx={{ fontWeight: 600 }}>
                {item.summary}
              </Typography>
              <Typography fontSize="14px" sx={{ whiteSpace: 'pre-wrap', mt: 2 }}>
                {item.releaseNote}
              </Typography>
            </div>
          ))
        ) : (
          <div className="p-4 bg-coolGrey-50 rounded-lg cursor-default">
            <Typography fontSize="14px">No customer-facing release notes were published for this month.</Typography>
          </div>
        )}
      </div>
    </div>
  );
}

export function ReleaseNotes() {
  const intl = useIntl();
  const [monthCount, setMonthCount] = useState(INITIAL_MONTH_COUNT);
  const { getReleaseNotes } = useReleaseNotesService();
  const setBreadcrumbs = useBreadcrumbStore(state => state.setBreadcrumbs);
  const loadMoreAnchorRef = useRef<HTMLDivElement | null>(null);
  const shouldScrollAfterLoadRef = useRef(false);

  const monthKeys = useMemo(() => getMonthKeys(monthCount), [monthCount]);
  const canLoadMoreMonths = hasMoreReleaseNotesMonths(monthKeys);

  useEffect(() => {
    setBreadcrumbs([
      {
        path: '/releasenotes',
        label: 'Release Notes',
      },
    ]);
  }, [intl, setBreadcrumbs]);

  const { data, isPending, isError, isFetching } = useQuery({
    queryKey: ['release_notes', monthKeys],
    queryFn: async (): Promise<ReleaseNotesMonth[]> => {
      const responses = await Promise.all(
        monthKeys.map(async month => {
          const response = await getReleaseNotes(month);
          if (response.error) {
            throw new Error(response.error.message);
          }
          return response.data!;
        })
      );

      return responses;
    },
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    if (!isFetching && data && shouldScrollAfterLoadRef.current) {
      loadMoreAnchorRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
      shouldScrollAfterLoadRef.current = false;
    }
  }, [data, isFetching]);

  if (isError) {
    throw new Error('Error encountered trying to load release notes');
  }

  return (
    <>
      <Container className="bg-snow-50 max-w-none w-full px-10 pt-6 flex flex-col sticky top-16 z-[39]">
        <Typography variant="h2" sx={{ fontWeight: 700, pb: 6 }} data-testid="release-notes-heading">
          TB.360 Release Notes
        </Typography>
      </Container>
      <Container className="standard-page-container gap-y-6 pt-6">
        {isPending && (
          <Tile>
            <MetricsLoadingSpinner />
          </Tile>
        )}
        {data?.map((month, index) => (
          <ReleaseNoteMonthSection key={month.month} month={month} highlight={index === 0} />
        ))}
        {canLoadMoreMonths && (
          <div className="col-span-12 flex justify-center pt-2">
            <Button
              priority="ghost"
              disabled={isFetching}
              onClick={() => {
                shouldScrollAfterLoadRef.current = true;
                setMonthCount(current => current + MONTHS_PER_PAGE);
              }}
              iconRight="ChevronDown"
            >
              Load more
            </Button>
          </div>
        )}
        <div ref={loadMoreAnchorRef} className="col-span-12 h-px" />
      </Container>
    </>
  );
}
